import { useEffect } from 'react';
import { useSelector } from '../redux/store';
import useAuth from './useAuth';

// The users list (practices/<id>/users/…) takes ~3s on production, and GeneralApp only renders the Launchpad once
// `isPractitioner` is false (not null) — so nothing on the Launchpad could even start until the list resolved.
// Same pattern as the old per-practice launchpad flag cache: persist the resolved role per practice + user and use it on the next load
// until the live list arrives (the live value always wins and rewrites the cache). It only decides WHICH dashboard
// renders — never a permission; the backend stays the authority. Keyed by practice: a user can be a Practitioner
// at one practice and an Admin at another.
export const roleCacheKey = (practiceId, userPk) => `dashboard:role:${practiceId}:${userPk}`;

const readCachedPractitioner = (practiceId, userPk) => {
    if (practiceId == null || userPk == null) return null;
    try {
        const v = localStorage.getItem(roleCacheKey(practiceId, userPk));
        if (v === 'Practitioner') return true;
        if (v === 'other') return false;
    } catch { /* ignore */ }
    return null;
};

export default function useGetCurrentUser() {
    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const practiceId = useSelector(state => state.practiceTreatmentList?.practiceId);
    const loadedPracticeId = useSelector(state => state.practiceUser.loadedPracticeId);
    const { user } = useAuth();

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);

    const isPractitionerLive = getUserList()[0]?.role === "Practitioner";
    const loaded = results.length > 0;

    // Only persist when the loaded list belongs to the CURRENT practice (during a practice switch `results` can still
    // hold the previous practice's users until the new load lands).
    const loadedForCurrentPractice = loadedPracticeId != null && practiceId != null && String(loadedPracticeId) === String(practiceId);

    useEffect(() => {
        if (!loaded || !loadedForCurrentPractice || user?.pk == null) return;
        try { localStorage.setItem(roleCacheKey(practiceId, user.pk), isPractitionerLive ? 'Practitioner' : 'other'); } catch { /* ignore */ }
    }, [loaded, loadedForCurrentPractice, isPractitionerLive, practiceId, user?.pk]);

    // Live value once the list has loaded; otherwise the cached role for THIS practice (null on a first-ever visit).
    const isPractitioner = loaded ? isPractitionerLive : readCachedPractitioner(practiceId, user?.pk);

    return { user: getUserList()?.[0] || null, isSuperUser: getUserList()[0]?.user?.is_superuser, isPractitioner };
}
